// One place that decides what colour a chart mark gets, so every screen tells the same
// story about the same category. The values live in styles/_variables.scss as
// --chart-* tokens (light and dark are separate, validated sets); this module only
// decides which slot a thing occupies.

// Fixed order. Slot 1 is always the first category, slot 2 always the second, and so on
// - the colour follows the entity, not its rank in the current filter, so narrowing a
// date range must never repaint the categories that survive it.
export const CHART_SLOTS = [
  'var(--chart-1)',
  'var(--chart-2)',
  'var(--chart-3)',
  'var(--chart-4)',
  'var(--chart-5)',
  'var(--chart-6)',
];

export const CHART_OTHER = 'var(--chart-other)';
export const CHART_GRID = 'var(--chart-grid)';
export const CHART_AHEAD = 'var(--chart-ahead)';
export const CHART_BEHIND = 'var(--chart-behind)';
export const CHART_ZERO = 'var(--chart-zero)';

export const MAX_CATEGORIES = CHART_SLOTS.length;

// Builds a stable name -> colour lookup. Anything past the sixth name shares the
// neutral "other" grey rather than wrapping back to slot 1: cycling a palette makes two
// unrelated categories identical, which is worse than admitting the tail is a remainder.
export function buildCategoryScale(names) {
  const scale = new Map();
  names.forEach((name, index) => {
    scale.set(name, index < MAX_CATEGORIES ? CHART_SLOTS[index] : CHART_OTHER);
  });
  return (name) => scale.get(name) || CHART_OTHER;
}

// Collapses a long tail into a single "Other" row. Six named slices plus a remainder is
// about the limit of what anyone reads off a chart; beyond that the small slices are
// noise that crowds out the ones that matter.
export function foldToTop(rows, { limit = MAX_CATEGORIES, otherLabel = 'Other', valueKey = 'value' } = {}) {
  const sorted = [...rows].sort((a, b) => b[valueKey] - a[valueKey]);
  if (sorted.length <= limit) return sorted;
  const head = sorted.slice(0, limit);
  const tail = sorted.slice(limit);
  return [...head, {
    ...tail[0],
    name: otherLabel,
    isOther: true,
    [valueKey]: tail.reduce((sum, row) => sum + row[valueKey], 0),
  }];
}
